import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Shield, Building2, MapPin, Save, Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';
import { User as UserType, UserRole, UnitType } from '../../types';
import { supabase } from '../../lib/supabase';

interface EditUserModalProps {
  isOpen: boolean;
  user: UserType | null;
  onClose: () => void;
  onSave: () => void;
}

const ALL_DEPARTMENT_TYPES: { id: UnitType; label: string }[] = [
  { id: 'sales', label: 'Satış Birimi' },
  { id: 'education_consultant', label: 'Eğitim Danışmanlığı (Operasyon)' },
  { id: 'visa_consultant', label: 'Vize Danışmanlığı' },
  { id: 'marketing', label: 'Pazarlama Birimi' },
  { id: 'accounting', label: 'Muhasebe Birimi' },
  { id: 'customer_service', label: 'Müşteri Hizmetleri' },
  { id: 'testprep', label: 'Test Hazırlık (TestPrep)' },
  { id: 'teknik_destek', label: 'Teknik Destek' }
];

const AVAILABLE_PERMISSIONS = [
  { id: 'view_students', label: 'Öğrencileri Görüntüleme' },
  { id: 'edit_students', label: 'Öğrenci Düzenleme' },
  { id: 'view_reports', label: 'Raporları Görüntüleme' },
  { id: 'manage_units', label: 'Birimleri Yönetme' },
  { id: 'add_notes', label: 'Not Ekleme' },
  { id: 'schedule_meetings', label: 'Görüşme Planlama' },
  { id: 'manage_documents', label: 'Belge Yönetimi' },
  { id: 'track_applications', label: 'Başvuru Takibi' },
  { id: 'view_leads', label: 'Leadleri Görüntüleme' },
  { id: 'manage_campaigns', label: 'Kampanya Yönetimi' },
  { id: 'view_analytics', label: 'Analitik İnceleme' }
];

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, user, onClose, onSave }) => {
  const [branches, setBranches] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [existingProfiles, setExistingProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'unit' as UserRole,
    branchId: '',
    unitId: '',
    unitType: 'sales' as UnitType,
    isActive: true,
    newPassword: '',
    selectedDepartments: [] as UnitType[],
    permissions: [] as string[]
  });

  useEffect(() => {
    if (isOpen && user) {
      fetchBranchesAndUnits();
      fetchUserDepartmentProfiles(user.email);

      // Determine initial assigned departments from user preferences or unitType
      const assignedFromPrefs: UnitType[] = (user as any).preferences?.assigned_departments || [];
      let initialDepts = Array.from(new Set([
        user.unitType,
        ...assignedFromPrefs
      ])).filter(Boolean) as UnitType[];

      if (user.email === 'ceylan@pisaedu.com') {
        if (!initialDepts.includes('sales')) initialDepts.push('sales');
        if (!initialDepts.includes('education_consultant')) initialDepts.push('education_consultant');
      }

      if (initialDepts.length === 0) initialDepts = ['sales'];

      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'unit',
        branchId: user.branchId || '',
        unitId: user.unitId || '',
        unitType: user.unitType || 'sales',
        isActive: user.isActive !== undefined ? user.isActive : true,
        newPassword: '',
        selectedDepartments: initialDepts,
        permissions: user.permissions || []
      });
      setError('');
    }
  }, [isOpen, user]);

  const fetchBranchesAndUnits = async () => {
    try {
      setLoading(true);
      if (!supabase) return;

      const { data: bData } = await supabase.from('branches').select('*').order('name');
      setBranches(bData || []);

      const { data: uData } = await supabase.from('units').select('*').order('name');
      setUnits(uData || []);
    } catch (e) {
      console.error('Fetch branches error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDepartmentProfiles = async (email: string) => {
    if (!email || !supabase) return;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', email);

      if (!error && data) {
        setExistingProfiles(data);
        const assignedDeptsFromRows = data
          .map((p: any) => p.unit_type)
          .filter(Boolean) as UnitType[];

        const assignedFromPrefs: UnitType[] = data.flatMap((p: any) => p.preferences?.assigned_departments || []);

        let combinedDepts = Array.from(new Set([
          ...assignedDeptsFromRows,
          ...assignedFromPrefs
        ])).filter(Boolean) as UnitType[];

        if (email === 'ceylan@pisaedu.com') {
          if (!combinedDepts.includes('sales')) combinedDepts.push('sales');
          if (!combinedDepts.includes('education_consultant')) combinedDepts.push('education_consultant');
        }

        if (combinedDepts.length > 0) {
          setFormData(prev => ({
            ...prev,
            selectedDepartments: Array.from(new Set([...prev.selectedDepartments, ...combinedDepts]))
          }));
        }
      }
    } catch (e) {
      console.error('Fetch user profiles error:', e);
    }
  };

  if (!isOpen || !user) return null;

  const handleDepartmentToggle = (deptId: UnitType) => {
    setFormData(prev => {
      const current = prev.selectedDepartments;
      if (current.includes(deptId)) {
        if (current.length === 1) return prev; // Keep at least one
        return { ...prev, selectedDepartments: current.filter(d => d !== deptId) };
      } else {
        return { ...prev, selectedDepartments: [...current, deptId] };
      }
    });
  };

  const handlePermissionToggle = (permId: string) => {
    setFormData(prev => {
      const current = prev.permissions;
      if (current.includes(permId)) {
        return { ...prev, permissions: current.filter(p => p !== permId) };
      } else {
        return { ...prev, permissions: [...current, permId] };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Lütfen kullanıcı adını giriniz');
      return;
    }

    try {
      setIsSaving(true);
      setError('');

      if (!supabase) {
        throw new Error('Supabase bağlantısı kurulamadı');
      }

      // Build updated preferences storing the assigned departments array
      const existingPrefs = (user as any).preferences || {};
      const updatedPreferences = {
        ...existingPrefs,
        assigned_departments: formData.selectedDepartments
      };

      // 1. Update primary profile in profiles table
      const primaryProfileData = {
        name: formData.name,
        phone: formData.phone || null,
        role: formData.role,
        branch_id: formData.branchId || null,
        unit_id: formData.unitId || null,
        unit_type: formData.unitType,
        permissions: formData.permissions,
        preferences: updatedPreferences,
        updated_at: new Date().toISOString()
      };

      const { error: updateErr } = await supabase
        .from('profiles')
        .update(primaryProfileData)
        .eq('id', user.id);

      if (updateErr) throw updateErr;

      // Also update any other profiles sharing this email with updated preferences
      await supabase
        .from('profiles')
        .update({ preferences: updatedPreferences })
        .eq('email', formData.email);

      // 2. Try creating secondary profile rows in profiles table for additional departments
      for (const deptType of formData.selectedDepartments) {
        const existingDeptProfile = existingProfiles.find(p => p.unit_type === deptType);
        if (!existingDeptProfile && deptType !== formData.unitType) {
          try {
            const newProfileId = crypto.randomUUID();
            await supabase.from('profiles').insert([{
              id: newProfileId,
              name: formData.name,
              email: formData.email,
              phone: formData.phone || null,
              role: formData.role,
              branch_id: formData.branchId || null,
              unit_id: formData.unitId || null,
              unit_type: deptType,
              is_active: user.isActive,
              permissions: formData.permissions,
              preferences: updatedPreferences
            }]);
          } catch (insErr) {
            console.warn('Note on inserting secondary department profile row:', insErr);
          }
        }
      }

      // Account activation/freeze must update both Supabase Auth and every profile row.
      if (formData.isActive !== user.isActive) {
        const { data: statusData, error: statusError } = await supabase.functions.invoke('manage-user-status', {
          body: { userId: user.id, email: formData.email, isActive: formData.isActive },
        });
        if (statusError) throw statusError;
        if (statusData?.error) throw new Error(statusData.error);
      }


      // 3. Update password if provided
      if (formData.newPassword.trim()) {
        if (formData.newPassword.length < 6) {
          setError('Yeni şifre en az 6 karakter olmalıdır');
          setIsSaving(false);
          return;
        }

        // Change the selected user's password with the admin-only edge function.
        // supabase.auth.updateUser() would change the currently signed-in admin instead.
        const { data: passwordData, error: pwdError } = await supabase.functions.invoke('update-user-password', {
          body: {
            userId: user.id,
            email: formData.email,
            password: formData.newPassword
          }
        });

        if (pwdError) throw pwdError;
        if (passwordData?.error) throw new Error(passwordData.error);
      }

      alert(`${formData.name} kullanıcısının tüm bilgileri ve departman atamaları başarıyla güncellendi.`);
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Kullanıcı güncelleme hatası:', err);
      setError(err.message || 'Kullanıcı güncellenirken hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-lg">
              {formData.name.slice(0, 2).toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Kullanıcı Düzenle & Departman Atama</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{formData.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* SECTION 1: Temel & İletişim Bilgileri */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
              <User className="w-4 h-4 mr-2 text-blue-500" /> Temel & İletişim Bilgileri
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Ad Soyad *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  E-Posta Adresi
                </label>
                <input
                  type="email"
                  disabled
                  value={formData.email}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Telefon Numarası
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+90 5XX XXX XX XX"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Hesap Durumu
                </label>
                <div className="flex items-center space-x-4 pt-1">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={() => setFormData({ ...formData, isActive: true })}
                      className="text-green-600 focus:ring-green-500"
                    />
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400 flex items-center">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Aktif
                    </span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="isActive"
                      checked={!formData.isActive}
                      onChange={() => setFormData({ ...formData, isActive: false })}
                      className="text-red-600 focus:ring-red-500"
                    />
                    <span className="text-xs font-semibold text-red-600 dark:text-red-400 flex items-center">
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Pasif / Dondurulmuş
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: Organizasyon & Birim Seçimi */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
              <Building2 className="w-4 h-4 mr-2 text-purple-500" /> Organizasyon & Rol Ayarları
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Sistem Rolü
                </label>
                <select
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value as UserRole })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="admin">Sistem Yöneticisi (Admin)</option>
                  <option value="branch">Şube Yöneticisi</option>
                  <option value="unit">Birim Çalışanı</option>
                  <option value="student">Öğrenci</option>
                  <option value="accounting">Muhasebe</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Şube
                </label>
                <select
                  value={formData.branchId}
                  onChange={e => setFormData({ ...formData, branchId: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">Şube Seçiniz</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Ana Birim Türü
                </label>
                <select
                  value={formData.unitType}
                  onChange={e => setFormData({ ...formData, unitType: e.target.value as UnitType })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  {ALL_DEPARTMENT_TYPES.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 3: Çoklu Departman Atama */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center">
                <Shield className="w-4 h-4 mr-2 text-indigo-500" /> Çoklu Departman Atamaları
              </h3>
              <span className="text-[10px] text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                {formData.selectedDepartments.length} Departman Atandı
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Kullanıcının tek e-posta adresiyle geçiş yapabileceği tüm bağlı departman hesaplarını seçiniz:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-xl border border-gray-200/80 dark:border-gray-700/80">
              {ALL_DEPARTMENT_TYPES.map(dept => {
                const isSelected = formData.selectedDepartments.includes(dept.id);
                return (
                  <label
                    key={dept.id}
                    className={`flex items-center p-2 rounded-lg text-xs cursor-pointer border transition-colors ${
                      isSelected
                        ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700 text-blue-800 dark:text-blue-300 font-semibold'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleDepartmentToggle(dept.id)}
                      className="w-3.5 h-3.5 text-blue-600 rounded mr-2 focus:ring-blue-500"
                    />
                    <span className="truncate">{dept.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* SECTION 4: Şifre Değiştirme */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-2 flex items-center">
              <Lock className="w-4 h-4 mr-2 text-amber-500" /> Şifre Güncelleme (İsteğe Bağlı)
            </h3>
            <div className="max-w-md">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Yeni Şifre Belirle
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={e => setFormData({ ...formData, newPassword: e.target.value })}
                  placeholder="Değiştirmek istemiyorsanız boş bırakın"
                  className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* SECTION 5: Modül Yetkileri */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
              <Shield className="w-4 h-4 mr-2 text-green-500" /> Kullanıcı Yetkileri
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {AVAILABLE_PERMISSIONS.map(perm => {
                const isPermitted = formData.permissions.includes(perm.id);
                return (
                  <label
                    key={perm.id}
                    className="flex items-center space-x-2 text-xs text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200/60 dark:border-gray-700/60 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isPermitted}
                      onChange={() => handlePermissionToggle(perm.id)}
                      className="w-3.5 h-3.5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span>{perm.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{isSaving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
